import { lazy, Suspense, useEffect } from "react";
import { AnimatePresence } from "framer-motion";
import { Route, Routes, useLocation } from "react-router-dom";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import CookieConsentBanner from "./components/CookieConsentBanner";
import CookiePreferencesModal from "./components/CookiePreferencesModal";
import CookieConsentProvider from "./components/CookieConsentProvider";
import CartProvider from "./components/CartProvider";
import AuthProvider from "./components/AuthProvider";
import EntryGate from "./components/EntryGate";
import { captureReferralFromUrl } from "./utils/referrals";
import DisclaimerBanner from "./components/DisclaimerBanner";
import EngagementWidgetStack from "./components/EngagementWidgetStack";
import InstallPrompt from "./components/InstallPrompt";
import OfflineBanner from "./components/OfflineBanner";
import SplashScreen from "./components/SplashScreen";
import LocationsSkeleton from "./components/LocationsSkeleton";
import Home from "./pages/Home";
import Welcome from "./pages/Welcome";
import Consent from "./pages/Consent";
import Aboard from "./pages/Aboard";
import ProfileSetup from "./pages/Profile";
import Charter from "./pages/Charter";
import DeliveryLocation from "./pages/DeliveryLocation";
import AddressBook from "./pages/AddressBook";
import Menu from "./pages/Menu";
import ProductPage from "./pages/ProductPage";
import DailyCatch from "./pages/DailyCatch";
import OrderDetail from "./pages/OrderDetail";
import OrderInvoice from "./pages/OrderInvoice";
import OrderReorder from "./pages/OrderReorder";
import OrderReview from "./pages/OrderReview";
import Account from "./pages/Account";
import EditProfile from "./pages/EditProfile";
import Orders from "./pages/Orders";
import ShareEarn from "./pages/ShareEarn";
import Favourites from "./pages/Favourites";
import RefundAssistance from "./pages/RefundAssistance";
import Complaint from "./pages/Complaint";
import HelpCenter from "./pages/HelpCenter";
import Faq from "./pages/Faq";
import Policies from "./pages/Policies";
import PrivacyData from "./pages/PrivacyData";
import AboutApp from "./pages/AboutApp";
import CatchPass from "./pages/CatchPass";
import PartyPlanner from "./pages/PartyPlanner";
import ComingSoon from "./pages/ComingSoon";
import ComingSoonIndex from "./pages/ComingSoonIndex";
import AdminShell from "./pages/admin/AdminShell";
import AdminDashboard from "./pages/admin/Dashboard";
import AdminFaq from "./pages/admin/AdminFaq";
import AdminTickets from "./pages/admin/AdminTickets";
import AdminServiceHours from "./pages/admin/AdminServiceHours";
import AdminSlots from "./pages/admin/AdminSlots";
import AdminBanners from "./pages/admin/AdminBanners";
import AdminAppSettings from "./pages/admin/AdminAppSettings";
import AdminCancelReasons from "./pages/admin/AdminCancelReasons";
import AdminPromotions from "./pages/admin/AdminPromotions";
import AdminZones from "./pages/admin/AdminZones";
import AdminFinance from "./pages/admin/AdminFinance";
import AdminPolicies from "./pages/admin/AdminPolicies";
import AdminPayments from "./pages/admin/AdminPayments";
import AdminReviews from "./pages/admin/AdminReviews";
import AdminTestimonials from "./pages/admin/AdminTestimonials";
import AdminRefunds from "./pages/admin/AdminRefunds";
import AdminDispatch from "./pages/admin/AdminDispatch";
import AdminDriverDetail from "./pages/admin/AdminDriverDetail";
import AdminDriverKyc from "./pages/admin/AdminDriverKyc";
import AdminDrivers from "./pages/admin/AdminDrivers";
import AdminCustomerDetail from "./pages/admin/AdminCustomerDetail";
import AdminCustomers from "./pages/admin/AdminCustomers";
import AdminProductNew from "./pages/admin/AdminProductNew";
import AdminProductDetail from "./pages/admin/AdminProductDetail";
import AdminProducts from "./pages/admin/AdminProducts";
import AdminBulkOrderDetail from "./pages/admin/AdminBulkOrderDetail";
import AdminBulkOrders from "./pages/admin/AdminBulkOrders";
import AdminOrderDetail from "./pages/admin/AdminOrderDetail";
import AdminOrders from "./pages/admin/AdminOrders";
import { AdminRequests } from "./pages/admin/AdminQueues";
import AdminSecurity from "./pages/admin/AdminSecurity";
import AdminTeam from "./pages/admin/AdminTeam";
import AdminSoon from "./pages/admin/AdminSoon";
import AdminLandings from "./pages/admin/AdminLandings";
import StaffLogin from "./pages/StaffLogin";
import Wallet from "./pages/Wallet";
import Categories from "./pages/Categories";
import About from "./pages/About";
import Products from "./pages/Products";
import Contact from "./pages/Contact";
import Compliance from "./pages/Compliance";
import Sustainability from "./pages/Sustainability";
import LegalRegistrations from "./pages/LegalRegistrations";
import Accessibility from "./pages/Accessibility";
import SuccessStories from "./pages/SuccessStories";
import CaseStudyDetail from "./pages/CaseStudyDetail";
import PressInvestors from "./pages/PressInvestors";
import Partnerships from "./pages/Partnerships";
import VulnerabilityDisclosurePolicy from "./pages/VulnerabilityDisclosurePolicy";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsAndConditions from "./pages/TermsAndConditions";
import Checkout from "./pages/Checkout";
import NotFound from "./pages/NotFound";
import PolicyDocument from "./pages/PolicyDocument";
import { trackEvent } from "./utils/analytics";

// Lazy: pulls in Leaflet + react-leaflet, which only /locations needs — kept
// out of the main chunk so every other route doesn't pay for map weight.
const Locations = lazy(() => import("./pages/Locations"));

export default function App() {
  const location = useLocation();
  // The login gateway, its follow-on steps and the account pages are self-contained: own header, no site chrome.
  const isAdmin = location.pathname === "/admin" || location.pathname.startsWith("/admin/");
  const isWelcome = location.pathname === "/welcome" || location.pathname.startsWith("/welcome/") || location.pathname.startsWith("/account/") || location.pathname === "/admin" || location.pathname.startsWith("/admin/");

  // trackEvent itself no-ops without consent/a configured GA4 ID (see
  // analytics.ts) — gtag's own automatic page_view only fires once, on
  // initial script load, which doesn't cover this app's client-side route
  // changes, so each one is reported explicitly here instead.
  // An invite link (…/welcome?ref=CODE) remembers the code so the sign-up profile step can pre-fill it.
  useEffect(() => captureReferralFromUrl(), []);

  useEffect(() => {
    trackEvent("page_view", { page_path: location.pathname });
  }, [location.pathname]);

  useEffect(() => {
    if (!location.hash) {
      window.scrollTo({ top: 0 });
      return;
    }

    // Respect an in-page anchor (e.g. /compliance#cookie-policy) instead of
    // always snapping to the top. Polled with rAF rather than looked up
    // once: AnimatePresence's mode="wait" keeps the outgoing page mounted
    // through its ~0.4s exit transition before the target route (and its
    // #cookie-policy element) ever mounts, so the element isn't in the DOM
    // yet at the instant this effect first runs on a cross-page hash link.
    let cancelled = false;
    const deadline = Date.now() + 600;
    const tryScroll = () => {
      if (cancelled) return;
      const el = document.getElementById(location.hash.slice(1));
      if (el) {
        el.scrollIntoView();
      } else if (Date.now() < deadline) {
        requestAnimationFrame(tryScroll);
      }
    };
    tryScroll();

    return () => {
      cancelled = true;
    };
  }, [location.pathname, location.hash]);

  return (
    <CookieConsentProvider>
      <AuthProvider>
        <CartProvider>
          <SplashScreen />
          {!isAdmin && <DisclaimerBanner />}
          <OfflineBanner />
          <div className="flex min-h-screen flex-col overflow-x-hidden">
            {!isWelcome && <Navbar />}
            {/* Routes must stay AnimatePresence's direct child — its add/remove
                detection keys off its immediate children, not anything nested
                deeper. Suspense wraps only the one lazy route's element instead
                of sitting between AnimatePresence and Routes, which would hide
                the location.pathname key from AnimatePresence and silently
                disable every page's exit transition, not just this route's. */}
            <AnimatePresence mode="wait">
              <Routes location={location} key={location.pathname}>
                <Route
                  path="/"
                  element={
                    <EntryGate>
                      <Home />
                    </EntryGate>
                  }
                />
                <Route path="/welcome" element={<Welcome />} />
                <Route path="/welcome/staff" element={<StaffLogin />} />
                <Route path="/admin" element={<AdminShell />}>
                  <Route index element={<AdminDashboard />} />
                  <Route path="orders" element={<AdminOrders />} />
                  <Route path="orders/:ref" element={<AdminOrderDetail />} />
                  <Route path="bulk-orders" element={<AdminBulkOrders />} />
                  <Route path="bulk-orders/:ref" element={<AdminBulkOrderDetail />} />
                  <Route path="customers" element={<AdminCustomers />} />
                  <Route path="drivers" element={<AdminDrivers />} />
                  <Route path="drivers/:id" element={<AdminDriverDetail />} />
                  <Route path="drivers/:id/kyc" element={<AdminDriverKyc />} />
                  <Route path="dispatch" element={<AdminDispatch />} />
                  <Route path="products" element={<AdminProducts />} />
                  <Route path="products/new" element={<AdminProductNew />} />
                  <Route path="products/:id" element={<AdminProductDetail />} />
                  <Route path="customers/:uid" element={<AdminCustomerDetail />} />
                  <Route path="claims" element={<AdminRefunds />} />
                  <Route path="faq" element={<AdminFaq />} />
                  <Route path="tickets" element={<AdminTickets />} />
                  <Route path="service-hours" element={<AdminServiceHours />} />
                  <Route path="slots" element={<AdminSlots />} />
                  <Route path="banners" element={<AdminBanners />} />
                  <Route path="app-settings" element={<AdminAppSettings />} />
                  <Route path="cancel-reasons" element={<AdminCancelReasons />} />
                  <Route path="promotions" element={<AdminPromotions />} />
                  <Route path="zones" element={<AdminZones />} />
                  <Route path="finance" element={<AdminFinance />} />
                  <Route path="policies" element={<AdminPolicies />} />
                  <Route path="payments" element={<AdminPayments />} />
                  <Route path="reviews" element={<AdminReviews />} />
                  <Route path="testimonials" element={<AdminTestimonials />} />
                  <Route path="requests" element={<AdminRequests />} />
                  <Route path="security" element={<AdminSecurity />} />
                  <Route path="team" element={<AdminTeam />} />
                  <Route path="landings" element={<AdminLandings />} />
                  <Route path="soon/:slug" element={<AdminSoon />} />
                </Route>
                <Route path="/welcome/consent" element={<Consent />} />
                <Route path="/welcome/aboard" element={<Aboard />} />
                <Route path="/welcome/profile" element={<ProfileSetup />} />
                <Route path="/welcome/terms" element={<Charter />} />
                <Route path="/welcome/delivery" element={<DeliveryLocation />} />
                <Route path="/account/addresses" element={<AddressBook />} />
                <Route path="/menu" element={<Menu />} />
                <Route path="/menu/:id" element={<ProductPage />} />
                <Route path="/daily-catch" element={<DailyCatch />} />
                <Route path="/profile" element={<Account />} />
                <Route path="/profile/edit" element={<EditProfile />} />
                <Route path="/wallet" element={<Wallet />} />
                <Route path="/complaint" element={<Complaint />} />
                <Route path="/help" element={<HelpCenter />} />
                <Route path="/faq" element={<Faq />} />
                <Route path="/coming-soon" element={<ComingSoonIndex />} />
                <Route path="/coming-soon/:slug" element={<ComingSoon />} />
                <Route path="/catch-pass" element={<CatchPass />} />
                <Route path="/party" element={<PartyPlanner />} />
                <Route path="/about-app" element={<AboutApp />} />
                <Route path="/policies" element={<Policies />} />
                <Route path="/account/privacy" element={<PrivacyData />} />
                <Route path="/refund-assistance" element={<RefundAssistance />} />
                <Route path="/favourites" element={<Favourites />} />
                <Route path="/share-earn" element={<ShareEarn />} />
                <Route path="/orders" element={<Orders />} />
                <Route path="/orders/:ref" element={<OrderDetail />} />
                <Route path="/orders/:ref/invoice" element={<OrderInvoice />} />
                <Route path="/orders/:ref/reorder" element={<OrderReorder />} />
                <Route path="/orders/:ref/review" element={<OrderReview />} />
                <Route path="/categories" element={<Categories />} />
                <Route path="/about" element={<About />} />
                <Route path="/products" element={<Products />} />
                <Route path="/compliance" element={<Compliance />} />
                <Route path="/sustainability" element={<Sustainability />} />
                <Route path="/legal" element={<LegalRegistrations />} />
                <Route path="/accessibility" element={<Accessibility />} />
                <Route
                  path="/locations"
                  element={
                    <Suspense fallback={<LocationsSkeleton />}>
                      <Locations />
                    </Suspense>
                  }
                />
                <Route path="/success-stories" element={<SuccessStories />} />
                <Route path="/success-stories/case-studies/:slug" element={<CaseStudyDetail />} />
                <Route path="/press-investors" element={<PressInvestors />} />
                <Route path="/partnerships" element={<Partnerships />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/security" element={<VulnerabilityDisclosurePolicy />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<TermsAndConditions />} />
                <Route path="/policy/:slug" element={<PolicyDocument />} />
                <Route path="/checkout" element={<Checkout />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </AnimatePresence>
            {!isWelcome && <Footer />}
            <CookieConsentBanner />
            <CookiePreferencesModal />
            {!isWelcome && <EngagementWidgetStack />}
            {!isWelcome && <InstallPrompt />}
          </div>
        </CartProvider>
      </AuthProvider>
    </CookieConsentProvider>
  );
}
